import { Fragment, memo, useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { Availability } from '@didaskill/domain/gamification/shared';
import type { QuestAchievementType } from '@didaskill/domain/user-achievements/constants';
import { questTitleMessages } from '@didaskill/ui/intl';
import { useBoolState } from '@didaskill/ui/utils';

import { useAvailability } from '../../../../hooks/useAvailability';
import { QuestTile } from '../../../quest-tile/QuestTile';
import { QuestDialog } from '../../../quests/quest-dialog/QuestDialog';

export type QuestRequirement = {
  /**
   * Title or description of the requirement.
   */
  title: string;

  /**
   * Whether the requirement has been completed.
   */
  isCompleted: boolean;
};

type QuestProps = {
  type: QuestAchievementType;
  progress: {
    total: number;
    completed: number;
  };
  requirements: Array<QuestRequirement>;
  availability?: Availability;
  accessStartDate: Date;
};

const QuestBase = ({ type, progress, requirements, availability, accessStartDate }: QuestProps) => {
  const intl = useIntl();

  const [isOpen, , , onToggle] = useBoolState(false);

  const isCompleted = useMemo(() => progress.completed === progress.total, [progress]);

  const { isLocked, availableAfterDays, nbDaysLeft, endOfQuest } = useAvailability({
    accessStartDate,
    availability,
  });

  return (
    <Fragment>
      <QuestTile
        key={type}
        type={type}
        progress={progress}
        isCompleted={isCompleted}
        onClick={onToggle}
        isLocked={isLocked}
        availableAfterDays={availableAfterDays}
        nbDaysLeft={nbDaysLeft}
      />

      <QuestDialog
        isOpen={isOpen}
        onClose={onToggle}
        requirements={requirements}
        achievementType={type}
        title={intl.formatMessage(questTitleMessages[type])}
        availableAfterDays={availableAfterDays}
        endOfQuest={endOfQuest}
      />
    </Fragment>
  );
};

QuestBase.displayName = 'Quest';

export const Quest = memo(QuestBase);
